import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, date, hhmm, canSeeCost, marginClass, STAGE_META } from "@/lib/format";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Split, ArrowLeftRight, Paperclip, Upload, Download, Trash2, Loader2, FileText, Image as ImageIcon, Pencil, Check, X } from "lucide-react";
import { Empty } from "@/components/Bits";
import { JobInfo } from "@/components/JobInfo";
import { SuperDelete } from "@/components/SuperDelete";
import { SuperEdit } from "@/components/SuperEdit";
import { useNavigate } from "react-router-dom";
import AssignPicker from "@/components/AssignPicker";
import { JobLinks } from "@/components/JobLinks";
import { canEdit as canEditRole, isAdmin } from "@/lib/format";
import { CheckCircle2, RotateCcw } from "lucide-react";
import LineItems from "@/components/LineItems";
import TimeEntries, { TIME_EDIT_ROLES } from "@/components/TimeEntries";

const STAGES = ["awaiting_work", "in_progress", "blocked", "ready_for_inspection", "ready_for_delivery", "completed"];
const BILLING = { fixed: "Fixed Price", hourly: "Hourly", per_unit: "Per Unit/Part", mixed: "Mixed" };

function InlineEdit({ value, type = "text", display, tooltip, onSave, className = "", testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input type={type} value={val} onChange={(e) => setVal(e.target.value)} data-testid={`${testid}-input`} autoFocus
          className="bg-background border border-primary/60 rounded px-1.5 py-0.5 text-sm w-32 text-foreground" />
        <button onClick={() => { onSave(val); setEditing(false); }} data-testid={`${testid}-save`} className="text-emerald-400"><Check className="w-4 h-4" /></button>
        <button onClick={() => { setVal(value); setEditing(false); }} className="text-red-400"><X className="w-4 h-4" /></button>
      </span>
    );
  }
  return (
    <span className={`group inline-flex items-center gap-1.5 cursor-pointer ${className}`} title={tooltip} onClick={() => setEditing(true)} data-testid={testid}>
      {display}<Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
    </span>
  );
}

function DescEdit({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  if (editing) return (
    <div className="max-w-2xl mt-1">
      <Textarea rows={3} value={val} onChange={(e) => setVal(e.target.value)} data-testid="edit-desc-input" autoFocus />
      <div className="flex gap-2 mt-1"><Button size="sm" onClick={() => { onSave(val); setEditing(false); }} data-testid="edit-desc-save">Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button></div>
    </div>
  );
  return (
    <p className="group text-sm text-muted-foreground max-w-2xl mt-1 cursor-pointer whitespace-pre-wrap" title="Job description — click to edit" onClick={() => setEditing(true)} data-testid="edit-desc">
      {value || <span className="italic">Add job description</span>}<Pencil className="inline w-3 h-3 ml-1 opacity-0 group-hover:opacity-60" />
    </p>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const cost = canSeeCost(user.role);
  const [job, setJob] = useState(null);
  const navTo = useNavigate();

  const load = useCallback(() => { api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => toast.error("Job not found")); }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveField = async (field, value) => {
    try { await api.patch(`/jobs/${id}`, { [field]: value }); toast.success("Saved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!job) return <div className="p-6 text-muted-foreground">Loading job…</div>;
  const done = job.status === "completed";
  const setDone = async (v) => {
    try { await api.patch(`/jobs/${id}`, { status: v ? "completed" : "open" }); toast.success(v ? `${job.job_number} moved to Completed Jobs` : `${job.job_number} reopened`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (user.role === "shop_floor") {
    return (
      <div className="p-4 sm:p-6 max-w-2xl" data-testid="job-card-floor">
        <Link to="/board" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" />Back</Link>
        <div className="card-industrial p-5"><JobInfo job={job} big /></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <Link to={done ? "/jobs/completed" : "/jobs"} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" />{done ? "Completed jobs" : "All jobs"}</Link>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-sm text-muted-foreground" data-testid="job-customer-name">{job.customer_name}</div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-extrabold">
              <InlineEdit value={job.job_name || ""} display={job.job_name || <span className="text-muted-foreground italic">Add job name</span>} tooltip="Job name — click to edit" onSave={(v) => saveField("job_name", v)} testid="edit-job-name" />
            </h1>
            {job.is_late && job.status !== "completed" && <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-300 border border-red-500/40">LATE</span>}
            {job.customer_supplied && <span className="text-xs px-2 py-1 rounded bg-sky-500/20 text-sky-300 border border-sky-500/40">Customer parts</span>}
            {done && <span className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" data-testid="job-completed-badge">COMPLETED {date(job.completed_at)}</span>}
            {canEditRole(user.role) && (done
              ? <Button size="sm" variant="outline" onClick={() => setDone(false)} data-testid="job-reopen-button"><RotateCcw className="w-4 h-4 mr-1" />Reopen</Button>
              : <Button size="sm" onClick={() => setDone(true)} data-testid="job-complete-button" className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1" />Mark Complete</Button>)}
            <SuperDelete entity="job" id={job.id} label={`job ${job.job_number} and all its stages, time and materials`} onDone={() => navTo("/jobs")} />
          </div>
          <p className="text-sm font-mono-num text-primary mt-1">{job.job_number}{job.job_type ? <span className="text-muted-foreground font-sans"> · {job.job_type}</span> : ""}</p>
          <DescEdit value={job.description} onSave={(v) => saveField("description", v)} />
        </div>
        <div className="text-right space-y-1">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{BILLING[job.billing_method]}</div>
          <InlineEdit value={job.quoted_value} type="number" display={money(job.quoted_value)} tooltip="Quoted value — click to edit" onSave={(v) => saveField("quoted_value", Number(v))} className="font-heading text-2xl font-extrabold" testid="edit-quoted" />
          <div className="text-xs text-muted-foreground flex items-center gap-1 justify-end">Due <InlineEdit value={job.due_date || ""} type="date" display={date(job.due_date)} tooltip="Due date — click to edit" onSave={(v) => saveField("due_date", v)} testid="edit-due" /> · {job.location}</div>
          {job.customer_po && <div className="text-xs text-muted-foreground">Cust PO: {job.customer_po}</div>}
        </div>
      </div>

      <JobLinks job={job} canEdit={canEditRole(user.role)} reload={load} />
      {cost && <JobItems job={job} canEdit={canEditRole(user.role)} reload={load} />}

      <Tabs defaultValue="production">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="production" data-testid="tab-production">Production</TabsTrigger>
          <TabsTrigger value="labour" data-testid="tab-labour">Time &amp; Notes</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">Materials &amp; Parts</TabsTrigger>
          {cost && <TabsTrigger value="purchasing" data-testid="tab-jobpurchasing">Purchasing</TabsTrigger>}
          {cost && <TabsTrigger value="costing" data-testid="tab-costing">Cost &amp; Margin</TabsTrigger>}
          {cost && <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>}
          <TabsTrigger value="files" data-testid="tab-files">Attachments</TabsTrigger>
        </TabsList>

        <TabsContent value="production"><Production job={job} reload={load} /></TabsContent>
        <TabsContent value="labour"><Labour jobId={id} /></TabsContent>
        <TabsContent value="materials"><Materials job={job} cost={cost} /></TabsContent>
        {cost && <TabsContent value="purchasing"><JobPurchasing jobId={id} /></TabsContent>}
        {cost && <TabsContent value="costing"><Costing jobId={id} /></TabsContent>}
        {cost && <TabsContent value="billing"><Billing job={job} reload={load} /></TabsContent>}
        <TabsContent value="files"><Files job={job} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Production({ job, reload }) {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newStage, setNewStage] = useState("");
  const [taskList, setTaskList] = useState([]);
  useEffect(() => {
    api.get("/config").then((r) => { setStaff(r.data.staff); setTaskList(r.data.task_list || []); });
    api.get(`/jobs/${job.id}/transfers`).then((r) => setTransfers(r.data));
  }, [job.id]);

  const staffName = (uid) => staff.find((s) => s.id === uid)?.name || "—";

  const setStatus = async (s, status) => { await api.patch(`/stages/${s.id}`, { status }); toast.success(`${s.name} → ${STAGE_META[status].label}`); reload(); };
  const assign = async (s, ids) => { try { await api.patch(`/stages/${s.id}`, { assigned_to: ids }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const delStage = async (s) => {
    if (!window.confirm(`Delete task "${s.name}" from this job? It goes to the Restore Bin for 30 days.`)) return;
    try { await api.delete(`/stages/${s.id}`); toast.success(`Task ${s.name} deleted`); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  const setQty = async (s, qty) => { await api.patch(`/stages/${s.id}`, { qty_completed: qty }); reload(); };
  const addStage = async () => { try { await api.post(`/jobs/${job.id}/stages`, { name: newStage }); setAddOpen(false); setNewStage(""); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const have = new Set(job.stages.map((s) => s.name));

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Tasks on this job. Split partial batches or transfer between sites.</p>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-stage-button"><Plus className="w-4 h-4 mr-1" />Add task</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">Add Task</DialogTitle></DialogHeader>
            <Select value={newStage} onValueChange={setNewStage}><SelectTrigger data-testid="new-stage-name"><SelectValue placeholder="Select task" /></SelectTrigger>
              <SelectContent>{taskList.filter((t) => !have.has(t)).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            <DialogFooter><Button onClick={addStage} disabled={!newStage} data-testid="add-stage-submit">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {job.stages.map((s) => (
        <div key={s.id} className="card-industrial p-4" data-testid={`stage-${s.id}`}>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <span className="font-heading font-bold">{s.name}</span>
              {s.batch_label && <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground">{s.batch_label}</span>}
              <span className={`text-[11px] px-2 py-0.5 rounded-full border font-mono ${STAGE_META[s.status].cls}`}>{STAGE_META[s.status].label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{s.location}</span>
              <span className="text-xs font-mono-num">Qty {s.qty_completed}/{s.qty_total}</span>
              <SuperEdit entity="stage" record={s} label="task" onDone={reload} />
              {isAdmin(user.role) && <Button size="icon" variant="ghost" onClick={() => delStage(s)} data-testid={`stage-delete-${s.id}`} title="Delete task" className="h-8 w-8 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 items-end">
            <div><Label className="text-[11px]">Status</Label>
              <Select value={s.status} onValueChange={(v) => setStatus(s, v)}><SelectTrigger data-testid={`stage-status-${s.id}`} className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((st) => <SelectItem key={st} value={st}>{STAGE_META[st].label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-[11px]">Assigned</Label>
              <AssignPicker staff={staff} value={s.assigned_to} onChange={(ids) => assign(s, ids)} testid={`stage-assign-${s.id}`} /></div>
            <div><Label className="text-[11px]">Qty complete</Label>
              <Input type="number" defaultValue={s.qty_completed} onBlur={(e) => setQty(s, e.target.value)} data-testid={`stage-qty-${s.id}`} className="mt-1 h-9" /></div>
            <div className="flex gap-2">
              <SplitDialog stage={s} reload={reload} />
              <TransferDialog stage={s} reload={reload} />
            </div>
          </div>
          {s.hold_reason && <div className="mt-2 text-xs text-red-400">Hold: {s.hold_reason}</div>}
          {(s.assigned_to || []).length > 0 && <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`stage-assigned-names-${s.id}`}>Assigned to {[s.assigned_to].flat(Infinity).map(staffName).join(", ")}</div>}
        </div>
      ))}

      {transfers.length > 0 && (
        <div className="card-industrial p-4">
          <h4 className="font-heading font-bold uppercase text-sm mb-2">Location Transfers</h4>
          <ul className="space-y-1.5 text-sm">
            {transfers.map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-muted-foreground">
                <ArrowLeftRight className="w-3.5 h-3.5" /> {t.quantity} units {t.from_location} → {t.to_location}
                <span className="text-xs">· {t.transferred_by} · {date(t.transferred_at)}</span>
                {t.note && <span className="text-xs italic">"{t.note}"</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SplitDialog({ stage, reload }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const submit = async () => {
    try { await api.post(`/stages/${stage.id}/split`, { qty: Number(qty) }); toast.success("Batch split"); setOpen(false); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="icon" variant="outline" className="h-9 w-9" data-testid={`stage-split-${stage.id}`} title="Split batch"><Split className="w-4 h-4" /></Button></DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">Split Batch — {stage.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Move some units into a separate batch that can advance independently. Total in this batch: {stage.qty_total}.</p>
        <Label className="text-xs">Units to split off</Label>
        <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid={`split-qty-${stage.id}`} />
        <DialogFooter><Button onClick={submit} data-testid={`split-submit-${stage.id}`}>Split</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ stage, reload }) {
  const [open, setOpen] = useState(false);
  const to = stage.location === "Rathcairn" ? "Athboy" : "Rathcairn";
  const [note, setNote] = useState("");
  const submit = async () => {
    try { await api.post(`/stages/${stage.id}/transfer`, { to_location: to, quantity: stage.qty_total, note }); toast.success(`Transferred to ${to}`); setOpen(false); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="icon" variant="outline" className="h-9 w-9" data-testid={`stage-transfer-${stage.id}`} title="Transfer site"><ArrowLeftRight className="w-4 h-4" /></Button></DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">Transfer to {to}</DialogTitle></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" data-testid={`transfer-note-${stage.id}`} />
        <DialogFooter><Button onClick={submit} data-testid={`transfer-submit-${stage.id}`}>Transfer {stage.qty_total} units</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JobItems({ job, canEdit, reload }) {
  const [lines, setLines] = useState(job.billing_lines || []);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setLines(job.billing_lines || []); setDirty(false); }, [job.billing_lines]);
  const save = async () => {
    try { await api.patch(`/jobs/${job.id}`, { billing_lines: lines.map((l) => ({ description: l.description, qty: Number(l.qty || 0), rate: Number(l.rate || 0) })) }); toast.success("Line items saved"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const useTotal = async (v) => { try { await api.patch(`/jobs/${job.id}`, { quoted_value: Number(v) }); toast.success("Quoted value updated"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  if (!canEdit) {
    if (!lines.length) return null;
    return <div className="card-industrial p-4 mb-6 text-sm" data-testid="job-items-readonly">{lines.map((l, i) => <div key={i} className="flex justify-between py-1"><span>{l.description} × {l.qty}</span><span className="font-mono-num">{money(l.amount)}</span></div>)}</div>;
  }
  return (
    <div className="card-industrial p-4 mb-6" data-testid="job-items-card">
      <LineItems lines={lines} onChange={(v) => { setLines(v); setDirty(true); }} quoted={job.quoted_value} onQuoted={useTotal} saveOnBlur testid="jd-items" />
      {dirty && <div className="flex justify-end mt-2"><Button size="sm" onClick={save} data-testid="jd-items-save" className="font-bold uppercase">Save line items</Button></div>}
    </div>
  );
}

function Labour({ jobId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/job-time", { params: { job_id: jobId } }).then((r) => setRows(r.data)), [jobId]);
  useEffect(() => { load(); }, [load]);
  const total = rows.reduce((s, r) => s + r.minutes, 0);
  if (rows.length === 0) return <Empty text="No time booked to this job yet." />;
  return (
    <div className="card-industrial overflow-hidden mt-4">
      <div className="px-4 py-3 flex justify-between bg-accent/40"><span className="font-heading font-bold uppercase text-sm">Time &amp; Employee Notes</span><span className="font-mono-num font-bold text-primary" data-testid="job-time-total">{hhmm(total)}</span></div>
      <TimeEntries rows={rows} showJob={false} canEdit={TIME_EDIT_ROLES.includes(user.role)} onChanged={load} testid="joblabour" />
    </div>
  );
}

function Materials({ job, cost }) {
  const { user } = useAuth();
  const canEditMat = canEditRole(user.role);
  const blankM = { description: "", unit: "ea", quantity: 0, qty_used: 0, wastage: 0, unit_cost: 0, supplier: "" };
  const blankP = { description: "", qty_received: 0, location: job.location, work_done: "", scrap_qty: 0, qty_delivered: 0, qty_returned: 0, damage_note: "" };
  const [mats, setMats] = useState([]);
  const [parts, setParts] = useState([]);
  const [mOpen, setMOpen] = useState(false);
  const [pOpen, setPOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [m, setM] = useState(blankM);
  const [p, setP] = useState(blankP);

  const load = useCallback(() => {
    api.get(`/jobs/${job.id}/materials`).then((r) => setMats(r.data));
    api.get(`/jobs/${job.id}/customer-parts`).then((r) => setParts(r.data));
  }, [job.id]);
  useEffect(() => { load(); }, [load]);

  const pick = (o, keys) => Object.fromEntries(keys.map((k) => [k, o[k] ?? ""]));
  const openM = (x) => { setEditId(x?.id || null); setM(x ? pick(x, Object.keys(blankM)) : blankM); setMOpen(true); };
  const openP = (x) => { setEditId(x?.id || null); setP(x ? pick(x, Object.keys(blankP)) : blankP); setPOpen(true); };
  const saveM = async () => {
    try {
      if (editId) await api.patch(`/materials/${editId}`, m); else await api.post("/materials", { ...m, job_id: job.id });
      toast.success(editId ? "Material updated" : "Material added"); setMOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveP = async () => {
    try {
      if (editId) await api.patch(`/customer-parts/${editId}`, p); else await api.post("/customer-parts", { ...p, job_id: job.id });
      toast.success(editId ? "Part updated" : "Customer part added"); setPOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (path, x) => {
    if (!window.confirm(`Delete "${x.description}"? It goes to the Restore Bin for 30 days.`)) return;
    try { await api.delete(`/${path}/${x.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const rowBtns = (path, x, open) => canEditMat && (
    <span className="flex shrink-0">
      <button onClick={() => open(x)} className="p-1.5 text-muted-foreground hover:text-primary transition-colors" title="Edit" data-testid={`${path}-edit-${x.id}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={() => del(path, x)} className="p-1.5 text-muted-foreground hover:text-red-400 transition-colors" title="Delete" data-testid={`${path}-delete-${x.id}`}><Trash2 className="w-4 h-4" /></button>
    </span>
  );
  const n = (v) => Math.round(Number(v || 0) * 1000) / 1000;

  return (
    <div className="mt-4 grid gap-6 lg:grid-cols-2">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-heading font-bold uppercase text-sm">MCK Materials</h4>
          <Dialog open={mOpen} onOpenChange={setMOpen}>
            <Button size="sm" variant="outline" onClick={() => openM(null)} data-testid="add-material-button"><Plus className="w-4 h-4 mr-1" />Add</Button>
            <DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">{editId ? "Edit Material" : "Add Material"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs">Description</Label><Input value={m.description} onChange={(e) => setM({ ...m, description: e.target.value })} data-testid="mat-desc" /></div>
                <div><Label className="text-xs">Unit</Label><Input value={m.unit} onChange={(e) => setM({ ...m, unit: e.target.value })} /></div>
                <div><Label className="text-xs">Supplier</Label><Input value={m.supplier} onChange={(e) => setM({ ...m, supplier: e.target.value })} /></div>
                <div><Label className="text-xs">Qty bought</Label><Input type="number" value={m.quantity} onChange={(e) => setM({ ...m, quantity: e.target.value })} data-testid="mat-qty" /></div>
                <div><Label className="text-xs">Qty used</Label><Input type="number" value={m.qty_used} onChange={(e) => setM({ ...m, qty_used: e.target.value })} /></div>
                <div><Label className="text-xs">Wastage</Label><Input type="number" value={m.wastage} onChange={(e) => setM({ ...m, wastage: e.target.value })} /></div>
                <div><Label className="text-xs">€/unit</Label><Input type="number" value={m.unit_cost} onChange={(e) => setM({ ...m, unit_cost: e.target.value })} data-testid="mat-cost" /></div>
              </div>
              <DialogFooter><Button onClick={saveM} disabled={!m.description} data-testid="mat-submit">{editId ? "Save" : "Add"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {mats.length === 0 ? <Empty text="No MCK materials." /> : (
          <div className="card-industrial divide-y divide-border">
            {mats.map((x) => (
              <div key={x.id} className="px-4 py-3" data-testid={`material-${x.id}`}>
                <div className="flex justify-between items-center gap-2"><span className="font-semibold text-sm flex-1">{x.description}</span>{cost && <span className="font-mono-num text-sm">{money(x.unit_cost * x.qty_used)}</span>}
                  {rowBtns("materials", x, openM)}</div>
                <div className="text-xs text-muted-foreground">Used {n(x.qty_used)}/{n(x.quantity)} {x.unit} · waste {n(x.wastage)} · left {n(x.remaining)} · {x.supplier}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-heading font-bold uppercase text-sm">Customer-Supplied Parts</h4>
          <Dialog open={pOpen} onOpenChange={setPOpen}>
            <Button size="sm" variant="outline" onClick={() => openP(null)} data-testid="add-part-button"><Plus className="w-4 h-4 mr-1" />Add</Button>
            <DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">{editId ? "Edit Customer Part" : "Customer Part"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs">Description</Label><Input value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} data-testid="part-desc" /></div>
                <div><Label className="text-xs">Qty received</Label><Input type="number" value={p.qty_received} onChange={(e) => setP({ ...p, qty_received: e.target.value })} data-testid="part-qty" /></div>
                <div><Label className="text-xs">Location</Label><Input value={p.location} onChange={(e) => setP({ ...p, location: e.target.value })} /></div>
                <div><Label className="text-xs">Scrap qty</Label><Input type="number" value={p.scrap_qty} onChange={(e) => setP({ ...p, scrap_qty: e.target.value })} /></div>
                <div><Label className="text-xs">Delivered</Label><Input type="number" value={p.qty_delivered} onChange={(e) => setP({ ...p, qty_delivered: e.target.value })} /></div>
                <div><Label className="text-xs">Returned</Label><Input type="number" value={p.qty_returned} onChange={(e) => setP({ ...p, qty_returned: e.target.value })} data-testid="part-returned" /></div>
                <div><Label className="text-xs">Damage note</Label><Input value={p.damage_note} onChange={(e) => setP({ ...p, damage_note: e.target.value })} data-testid="part-damage" /></div>
                <div className="col-span-2"><Label className="text-xs">Work done / notes</Label><Textarea value={p.work_done} onChange={(e) => setP({ ...p, work_done: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter><Button onClick={saveP} disabled={!p.description} data-testid="part-submit">{editId ? "Save" : "Add"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {parts.length === 0 ? <Empty text="No customer parts (not treated as MCK purchases)." /> : (
          <div className="card-industrial divide-y divide-border">
            {parts.map((x) => (
              <div key={x.id} className="px-4 py-3" data-testid={`part-${x.id}`}>
                <div className="flex justify-between items-center gap-2"><span className="font-semibold text-sm flex-1">{x.description}</span>{rowBtns("customer-parts", x, openP)}</div>
                <div className="text-xs text-muted-foreground">Recd {n(x.qty_received)} @ {x.location} · scrap {n(x.scrap_qty)} · delivered {n(x.qty_delivered)} · returned {n(x.qty_returned)}</div>
                {x.work_done && <div className="text-xs text-muted-foreground mt-1 italic">{x.work_done}</div>}
                {x.damage_note && <div className="text-xs text-amber-400 mt-1">⚠ {x.damage_note}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobPurchasing({ jobId }) {
  const [pos, setPos] = useState([]);
  const [inv, setInv] = useState([]);
  useEffect(() => {
    api.get("/supplier-pos", { params: { job_id: jobId } }).then((r) => setPos(r.data));
    api.get("/supplier-invoices", { params: { job_id: jobId } }).then((r) => setInv(r.data));
  }, [jobId]);
  return (
    <div className="mt-4 space-y-6">
      <div>
        <h4 className="font-heading font-bold uppercase text-sm mb-2">Supplier POs (MCK-raised)</h4>
        {pos.length === 0 ? <Empty text="No supplier POs for this job." /> : pos.map((po) => (
          <div key={po.id} className="card-industrial p-3 mb-2 flex justify-between text-sm" data-testid={`jobpo-${po.po_number}`}>
            <span><span className="font-mono-num text-primary font-bold">{po.po_number}</span> · {po.supplier} <span className="text-xs text-muted-foreground">({po.status.replace("_", " ")})</span></span>
            <span className="font-mono-num">{money(po.lines.filter((l) => l.job_id === jobId).reduce((s, l) => s + l.line_total, 0))}</span>
          </div>
        ))}
      </div>
      <div>
        <h4 className="font-heading font-bold uppercase text-sm mb-2">Supplier Invoices allocated here</h4>
        {inv.length === 0 ? <Empty text="No supplier invoices allocated." /> : inv.map((x) => (
          <div key={x.id} className="card-industrial p-3 mb-2 flex justify-between text-sm" data-testid={`jobsi-${x.invoice_number}`}>
            <span><span className="font-mono-num font-bold">{x.invoice_number}</span> · {x.supplier}</span>
            <span className="font-mono-num">{money((x.allocations.find((a) => a.job_id === jobId) || {}).amount || 0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Costing({ jobId }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/jobs/${jobId}/costing`).then((r) => setC(r.data)); }, [jobId]);
  if (!c) return <div className="mt-4 text-muted-foreground">Loading costing…</div>;
  const Row = ({ label, value, strong, accent, testid }) => (
    <div className={`flex justify-between px-4 py-2.5 ${strong ? "bg-accent/40 font-bold" : ""}`} data-testid={testid}>
      <span className={strong ? "" : "text-muted-foreground text-sm"}>{label}</span>
      <span className={`font-mono-num ${accent || ""}`}>{money(value)}</span>
    </div>
  );
  return (
    <div className="mt-4 grid gap-6 lg:grid-cols-2">
      <div className="card-industrial overflow-hidden divide-y divide-border">
        <div className="px-4 py-3 font-heading font-bold uppercase text-sm">Cost to Date &amp; Projection</div>
        <Row label="Labour" value={c.labour_cost} testid="cost-labour" />
        <Row label="Materials (MCK)" value={c.materials_cost} testid="cost-materials" />
        <Row label="Supplier invoices" value={c.invoiced_purchase_cost} testid="cost-invoiced" />
        <Row label="Actual cost to date" value={c.actual_cost} strong testid="cost-actual" />
        <Row label="Committed (POs not yet invoiced)" value={c.committed_cost} testid="cost-committed" />
        <Row label="Expected cost to complete" value={c.expected_cost_to_complete} testid="cost-etc" />
        <Row label="Projected final cost" value={c.projected_final_cost} strong testid="cost-projected" />
      </div>
      <div className="card-industrial overflow-hidden divide-y divide-border">
        <div className="px-4 py-3 font-heading font-bold uppercase text-sm">Value &amp; Margin</div>
        <Row label="Quoted value" value={c.quoted_value} testid="val-quoted" />
        <Row label="Agreed variations" value={c.variations_total} testid="val-variations" />
        <Row label="Contract value" value={c.contract_value} strong testid="val-contract" />
        <Row label="Billed to date" value={c.billed_to_date} testid="val-billed" />
        <Row label="Uninvoiced work" value={c.uninvoiced_work} accent="text-amber-400" testid="val-uninvoiced" />
        <Row label="Estimated margin (at quote)" value={c.estimated_margin} testid="margin-est" />
        <div className="flex justify-between px-4 py-3 bg-accent/40 font-bold" data-testid="margin-projected">
          <span>Projected margin</span>
          <span className={`font-mono-num ${marginClass(c.margin_pct)}`}>{money(c.projected_margin)} · {c.margin_pct}%</span>
        </div>
        <div className="px-4 py-2 text-[11px] text-muted-foreground">{c.labour_hours}h labour booked. Figures update live from time, materials and committed/actual purchases.</div>
      </div>
      {c.linked_jobs?.length > 0 && <LinkedCosting c={c} Row={Row} />}
    </div>
  );
}

function LinkedCosting({ c, Row }) {
  const k = c.combined;
  return (
    <div className="card-industrial overflow-hidden divide-y divide-border lg:col-span-2" data-testid="linked-costing">
      <div className="px-4 py-3 font-heading font-bold uppercase text-sm">Including Linked Jobs</div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="text-[11px] font-mono uppercase text-muted-foreground"><tr><th className="text-left px-4 py-2">Job</th><th className="text-right px-4 py-2">Labour</th><th className="text-right px-4 py-2">Materials</th><th className="text-right px-4 py-2">Purchases</th><th className="text-right px-4 py-2">Actual</th><th className="text-right px-4 py-2">Projected</th></tr></thead>
        <tbody className="divide-y divide-border">
          {[{ job_number: "This job", id: "self", ...c }, ...c.linked_jobs].map((l) => (
            <tr key={l.id} data-testid={`linked-cost-${l.job_number}`}><td className="px-4 py-2">{l.id === "self" ? "This job" : <Link to={`/jobs/${l.id}`} className="text-primary hover:underline">{l.job_name || l.job_number}</Link>}</td>
              {["labour_cost", "materials_cost", "invoiced_purchase_cost", "actual_cost", "projected_final_cost"].map((x) => <td key={x} className="px-4 py-2 text-right font-mono-num">{money(l[x])}</td>)}</tr>
          ))}
        </tbody>
      </table></div>
      <Row label="Combined actual cost" value={k.actual_cost} strong testid="combined-actual" />
      <Row label="Combined projected final cost" value={k.projected_final_cost} strong testid="combined-projected" />
      <div className="flex justify-between px-4 py-3 bg-accent/40 font-bold" data-testid="combined-margin">
        <span>Projected margin incl. linked jobs</span>
        <span className={`font-mono-num ${marginClass(k.margin_pct)}`}>{money(k.projected_margin)} · {k.margin_pct}%</span>
      </div>
    </div>
  );
}

function Billing({ job, reload }) {
  const [vars, setVars] = useState([]);
  const [rti, setRti] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [vOpen, setVOpen] = useState(false);
  const [rOpen, setROpen] = useState(false);
  const [v, setV] = useState({ description: "", amount: 0 });
  const [r, setR] = useState({ type: "milestone", description: "", qty: 0, amount: 0 });

  const load = useCallback(() => {
    api.get(`/jobs/${job.id}/variations`).then((x) => setVars(x.data));
    api.get("/ready-to-invoice", { params: { job_id: job.id } }).then((x) => setRti(x.data));
    api.get("/invoices", { params: { job_id: job.id } }).then((x) => setInvoices(x.data));
  }, [job.id]);
  useEffect(() => { load(); }, [load]);

  const addVar = async () => { await api.post("/variations", { ...v, job_id: job.id }); toast.success("Variation added"); setVOpen(false); load(); reload(); };
  const addRti = async () => { await api.post("/ready-to-invoice", { ...r, job_id: job.id }); toast.success("Sent to Ready to Invoice"); setROpen(false); load(); };

  return (
    <div className="mt-4 grid gap-6 lg:grid-cols-2">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-heading font-bold uppercase text-sm">Variations &amp; Extras</h4>
          <Dialog open={vOpen} onOpenChange={setVOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-variation-button"><Plus className="w-4 h-4 mr-1" />Add</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">Agreed Variation</DialogTitle></DialogHeader>
              <Label className="text-xs">Description</Label><Input value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="var-desc" />
              <Label className="text-xs">Amount (€)</Label><Input type="number" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} data-testid="var-amount" />
              <DialogFooter><Button onClick={addVar} disabled={!v.description} data-testid="var-submit">Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {vars.length === 0 ? <Empty text="No variations." /> : (
          <div className="card-industrial divide-y divide-border">
            {vars.map((x) => <div key={x.id} className="px-4 py-2.5 flex justify-between text-sm" data-testid={`variation-${x.id}`}><span>{x.description}</span><span className="font-mono-num">{money(x.amount)}</span></div>)}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-heading font-bold uppercase text-sm">Ready to Invoice</h4>
          <Dialog open={rOpen} onOpenChange={setROpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-rti-button"><Plus className="w-4 h-4 mr-1" />Send</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle className="font-heading uppercase">Send to Ready to Invoice</DialogTitle></DialogHeader>
              <Label className="text-xs">Type</Label>
              <Select value={r.type} onValueChange={(val) => setR({ ...r, type: val })}><SelectTrigger data-testid="rti-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="hours">Hours</SelectItem><SelectItem value="quantity">Delivered Qty</SelectItem><SelectItem value="milestone">Milestone</SelectItem><SelectItem value="extra">Agreed Extra</SelectItem></SelectContent></Select>
              <Label className="text-xs">Description</Label><Input value={r.description} onChange={(e) => setR({ ...r, description: e.target.value })} data-testid="rti-desc" />
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Qty</Label><Input type="number" value={r.qty} onChange={(e) => setR({ ...r, qty: e.target.value })} /></div>
                <div><Label className="text-xs">Amount (€)</Label><Input type="number" value={r.amount} onChange={(e) => setR({ ...r, amount: e.target.value })} data-testid="rti-amount" /></div>
              </div>
              <DialogFooter><Button onClick={addRti} disabled={!r.description} data-testid="rti-submit">Send</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {rti.length === 0 ? <Empty text="Nothing queued." /> : (
          <div className="card-industrial divide-y divide-border">
            {rti.map((x) => <div key={x.id} className="px-4 py-2.5 flex justify-between text-sm items-center" data-testid={`jobrti-${x.id}`}><span>{x.description}</span><span className="flex items-center gap-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-accent uppercase">{x.status}</span><span className="font-mono-num">{money(x.amount)}</span></span></div>)}
          </div>
        )}
        {invoices.length > 0 && (
          <div className="mt-4">
            <h4 className="font-heading font-bold uppercase text-sm mb-2">Invoices</h4>
            <div className="card-industrial divide-y divide-border">
              {invoices.map((x) => <div key={x.id} className="px-4 py-2.5 flex justify-between text-sm" data-testid={`jobinvoice-${x.invoice_number}`}><span className="font-mono-num font-bold">{x.invoice_number}</span><span className="font-mono-num">{money(x.total)}</span></div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentThumb({ a, onOpen }) {
  const [thumb, setThumb] = useState(null);
  const isImg = (a.content_type || "").startsWith("image/");
  const isPdf = (a.content_type || "") === "application/pdf";
  useEffect(() => {
    let url;
    if (isImg && a.storage_path) {
      api.get(`/files/${a.storage_path}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setThumb(url); }).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [a.storage_path, isImg]);

  return (
    <button onClick={() => onOpen(a)} data-testid={`attachment-thumb-${a.id}`}
      className="w-full aspect-[4/3] rounded-lg border border-border bg-accent/30 overflow-hidden flex items-center justify-center hover:border-primary/50 transition-colors group relative">
      {isImg && thumb ? (
        <img src={thumb} alt={a.name} className="w-full h-full object-cover" />
      ) : isPdf ? (
        <FileText className="w-10 h-10 text-red-400" />
      ) : isImg ? (
        <ImageIcon className="w-10 h-10 text-sky-400" />
      ) : (
        <Paperclip className="w-10 h-10 text-muted-foreground" />
      )}
      <span className="absolute inset-x-0 bottom-0 bg-black/60 text-[10px] px-1.5 py-0.5 text-white truncate">{a.name}</span>
    </button>
  );
}

function Files({ job, reload }) {
  const fileRef = useRef(null);
  const [kind, setKind] = useState("drawing");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {url, type, name}

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      await api.post(`/jobs/${job.id}/attachments/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      reload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openPreview = async (a) => {
    if (!a.storage_path) return;
    try {
      const res = await api.get(`/files/${a.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreview({ url, type: a.content_type || res.data.type, name: a.name });
    } catch (e) { toast.error("Could not open file"); }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };

  const del = async (a) => {
    try { await api.delete(`/jobs/${job.id}/attachments/${a.id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const isImg = (preview?.type || "").startsWith("image/");

  return (
    <div className="mt-4">
      <div className="card-industrial p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Label className="text-xs">Document type</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="att-kind" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="drawing">Drawing</SelectItem>
              <SelectItem value="supplier_invoice">Supplier Invoice</SelectItem>
              <SelectItem value="delivery_docket">Delivery Docket</SelectItem>
              <SelectItem value="certificate">Certificate</SelectItem>
              <SelectItem value="document">Document</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <input ref={fileRef} type="file" className="hidden" data-testid="att-file-input"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.txt,.doc,.docx,.xls,.xlsx,.dwg,.dxf"
          onChange={(e) => doUpload(e.target.files?.[0])} />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="att-upload-button" className="font-bold uppercase">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Upload File
        </Button>
        <p className="text-[11px] text-muted-foreground">PDF, images, drawings, dockets or certs — up to 25MB. Tap a tile to preview on the shop floor.</p>
      </div>

      {(!job.attachments || job.attachments.length === 0) ? <Empty text="No attachments yet. Upload drawings, dockets, certs or supplier invoices." /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {job.attachments.map((a) => (
            <div key={a.id} className="card-industrial p-2.5" data-testid={`attachment-${a.id}`}>
              <AttachmentThumb a={a} onOpen={openPreview} />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground uppercase">{a.kind}</span>
                <div className="flex items-center gap-2">
                  {a.storage_path && (
                    <button onClick={() => openPreview(a)} className="text-muted-foreground hover:text-primary" data-testid={`attachment-download-${a.id}`} title="Preview / open"><Download className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => del(a)} className="text-muted-foreground hover:text-red-400" data-testid={`attachment-delete-${a.id}`} title="Remove"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 truncate">{a.uploaded_by} · {date(a.uploaded_at)}</div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading uppercase truncate">{preview?.name}</DialogTitle></DialogHeader>
          {preview && (isImg ? (
            <img src={preview.url} alt={preview.name} className="w-full max-h-[70vh] object-contain rounded-lg" data-testid="preview-image" />
          ) : (
            <iframe src={preview.url} title={preview.name} className="w-full h-[70vh] rounded-lg bg-white" data-testid="preview-frame" />
          ))}
          <DialogFooter>
            <a href={preview?.url} target="_blank" rel="noreferrer" data-testid="preview-open-tab"><Button variant="outline">Open in new tab</Button></a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
